import { z } from 'zod';
import { dossierTotals, lineTotals, type CatalogUnit } from './catalog';
import {
  CONSENT_CONDITIONS,
  SALE_CHANNELS,
  conditionMet,
  customerTypeOf,
  legalVars,
  renderLegalText,
  withdrawalRight,
  type CompanyLegal,
  type ConsentCondition,
  type SaleChannel,
} from './legal';
const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'UUID invalide');

/**
 * Récapitulatif, consentements, signature et contrat figé (CDC §4.11 – §4.13, §20, §22).
 *
 * Le contrat est un INSTANTANÉ canonique (JSON aux clés triées) de ce que le client signe :
 * entreprise, client, adresse, produits et prix, conditions d'installation, réserves, textes
 * juridiques (versions exactes) et consentements. L'appareil et le serveur calculent la même
 * empreinte SHA-256 : si le dossier a changé entre-temps (prix fixé par le serveur, autre
 * modification…), le serveur refuse et il faut faire signer à nouveau.
 */

export const LEGAL_TEXT_KINDS = ['terms', 'consent', 'reserve', 'privacy', 'withdrawal'] as const;
export type LegalTextKind = (typeof LEGAL_TEXT_KINDS)[number];
export const LEGAL_TEXT_KIND_LABELS: Record<LegalTextKind, string> = {
  terms: 'Conditions générales de vente',
  consent: 'Cases à cocher (consentements)',
  reserve: 'Réserves facultatives',
  privacy: 'Données personnelles (RGPD)',
  withdrawal: 'Formulaire de rétractation',
};

export interface LegalTextRecord {
  id: string;
  version: number;
  updatedAt: string;
  kind: LegalTextKind;
  code: string;
  /** numéro de version du texte (1, 2, …) — un texte publié n'est jamais modifié */
  textVersion: number;
  title: string;
  body: string;
  /** case obligatoire pour signer (lorsqu'elle est affichée) */
  required: boolean;
  /** validé juridiquement (§29) — exigé en production */
  reviewed: boolean;
  /** activé / désactivé par l'administrateur : un texte désactivé n'est plus présenté */
  isActive: boolean;
  /** condition d'affichage (consentements) — voir CONSENT_CONDITIONS */
  condition: ConsentCondition;
  /** base légale (RGPD) ou fondement de la case */
  legalBasis: string | null;
  /** ex. « marketing » : une case acceptée met à jour le consentement du client */
  category: string | null;
  /** réserves : commentaire obligatoire / photo exigée */
  requiresComment: boolean;
  requiresPhoto: boolean;
  sortOrder: number;
  createdAt: string;
}

export const legalTextInput = z.object({
  kind: z.enum(LEGAL_TEXT_KINDS),
  code: z.string().regex(/^[a-z0-9_]{2,50}$/, 'Code : minuscules, chiffres, _'),
  title: z.string().trim().min(2).max(200),
  body: z.string().trim().min(1).max(40_000),
  required: z.boolean().default(false),
  reviewed: z.boolean().default(false),
  isActive: z.boolean().default(true),
  condition: z.enum(CONSENT_CONDITIONS).default('always'),
  legalBasis: z.string().trim().max(200).nullish().transform((v) => v || null),
  category: z.string().trim().max(50).nullish().transform((v) => v || null),
  requiresComment: z.boolean().default(false),
  requiresPhoto: z.boolean().default(false),
  sortOrder: z.number().int().default(0),
});

/* ------------------------------------------------------------------ réserves (§4.11) */

/**
 * Réserve choisie dans la bibliothèque : le TEXTE juridique vient du modèle (non modifiable par
 * le commercial) ; seuls les champs structurés sont saisis sur place (commentaire ; support et
 * hauteur sont repris du dossier).
 */
export const reserveEntry = z.object({
  textId: uuid,
  comment: z.string().trim().max(2000).nullish().transform((v) => v || null),
});
export type ReserveEntry = z.infer<typeof reserveEntry>;
export const reservesSchema = z.array(reserveEntry).max(20);

/* ------------------------------------------------------------------ signature */

/**
 * Tracé vectoriel : traits de points [x, y] normalisés sur la LARGEUR de la zone de signature
 * (0..10000 pour x, y dans la même échelle) — indépendant de l'écran, proportions conservées.
 */
export const strokesSchema = z
  .array(z.array(z.tuple([z.number().int().min(0).max(10_000), z.number().int().min(0).max(10_000)])).min(1).max(2000))
  .min(1, 'Signature vide')
  .max(200)
  .refine((s) => s.reduce((n, t) => n + t.length, 0) <= 10_000, 'Signature trop complexe')
  .refine((s) => s.reduce((n, t) => n + t.length, 0) >= 5, 'Signature trop courte');
export type Strokes = z.infer<typeof strokesSchema>;

export const SIGNER_QUALITIES = ['client', 'representative'] as const;
export const SIGNER_QUALITY_LABELS = { client: 'Le client', representative: 'Représentant du client' } as const;

export const signInput = z.object({
  signatureId: uuid,
  signerName: z.string().trim().min(2, 'Nom du signataire obligatoire').max(200),
  signerQuality: z.enum(SIGNER_QUALITIES).default('client'),
  /** textes affichés au client (versions exactes) */
  termIds: z.array(uuid).max(20),
  presentedConsentIds: z.array(uuid).max(30),
  privacyId: uuid.nullable(),
  withdrawalFormId: uuid.nullable(),
  /** cases cochées par le client */
  consentIds: z.array(uuid).max(30),
  strokes: strokesSchema,
  /** empreinte du contrat calculée par l'appareil au moment de la signature */
  contentHash: z.string().regex(/^[0-9a-f]{64}$/),
  signedAt: z.string().datetime(),
});
export type SignInput = z.input<typeof signInput>;

export const voidSignatureInput = z.object({ reason: z.string().trim().min(3, 'Motif obligatoire').max(1000) });

export interface SignatureSummary {
  id: string;
  method: 'onsite' | 'portal';
  signerName: string;
  signedAt: string;
  contentHash: string;
  /** PDF du bon de commande signé (document du dossier) */
  fileId: string | null;
  /** « Conditions générales et annexes » remises avec le bon de commande */
  annexesFileId?: string | null;
  /** total TTC signé (centimes) : plafond des encaissements ; null tant que non confirmé par le serveur */
  totalTtcCents: number | null;
}

/** Champs du dossier repris dans le contrat : verrouillés tant que la signature est valide. */
export const CONTRACT_DOSSIER_FIELDS = [
  'dossierTypeId',
  'siteAddressLine1',
  'siteAddressLine2',
  'sitePostalCode',
  'siteCity',
  'installWallTypeId',
  'installAtHeight',
  'installAccess',
  'installHeightM',
  'reserves',
  'saleChannel',
  'proEmployeeCount',
  'proOutsideMainActivity',
  'earlyStartRequested',
] as const;

/* ------------------------------------------------------------------ contenu canonique */

type TextRef = Pick<LegalTextRecord, 'id' | 'kind' | 'code' | 'textVersion' | 'title' | 'body' | 'required' | 'condition' | 'isActive' | 'category'> &
  Partial<Pick<LegalTextRecord, 'requiresComment' | 'requiresPhoto'>>;

export interface ContractSource {
  company: CompanyLegal | null | undefined;
  dossier: {
    id: string;
    typeLabel: string;
    siteAddressLine1: string | null;
    siteAddressLine2: string | null;
    sitePostalCode: string | null;
    siteCity: string | null;
    installWallTypeLabel: string | null;
    installAtHeight: boolean | null;
    installAccess: string | null;
    installHeightM: number | null;
    reserves: readonly ReserveEntry[];
    saleChannel: SaleChannel;
    proEmployeeCount: number | null;
    proOutsideMainActivity: boolean | null;
    earlyStartRequested: boolean;
  };
  client: {
    kind: string;
    civility: string | null;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
    siret: string | null;
    email: string | null;
    phone: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    postalCode: string | null;
    city: string | null;
  };
  lines: readonly {
    id: string;
    sortOrder: number;
    label: string;
    model: string | null;
    unit: CatalogUnit;
    quantity: number;
    unitPriceHtCents: number;
    vatRateBp: number;
    /** remise HT accordée sur la ligne (0 / absent = aucune) */
    discountHtCents?: number | null;
    /** ancien prix unitaire HT affiché barré (absent = aucun) */
    compareAtPriceHtCents?: number | null;
    /** ancien prix unitaire TTC exact (affiché barré au client) */
    compareAtPriceTtcCents?: number | null;
    /** prix TTC exact (absent = calculé depuis le HT) */
    unitPriceTtcCents?: number | null;
    discountTtcCents?: number | null;
    /** mise en avant dans un encadré (ex. extension de garantie) */
    quoteHighlight?: boolean | null;
    optionSteps: readonly string[];
  }[];
  /** textes PRÉSENTÉS (voir presentedTexts) et modèles des réserves du dossier */
  texts: { terms: readonly TextRef[]; consents: readonly TextRef[]; privacy: TextRef | null; withdrawal: TextRef | null; reserves: readonly TextRef[] };
  acceptedConsentIds: readonly string[];
  signer: { name: string; quality: string };
  /** date de signature (ISO) — reprise dans le formulaire de rétractation */
  signedAt: string;
}

const txt = (v: unknown): string | null => (typeof v === 'string' ? v.trim().replace(/\s+/g, ' ') || null : null);
const ACCESS = { none: 'aucun', ladder: 'échelle', nacelle: 'nacelle' } as Record<string, string>;
const clientName = (c: ContractSource['client']) => txt(c.companyName) ?? ([c.civility === 'MME' ? 'Mme' : c.civility === 'M' ? 'M.' : null, txt(c.firstName), txt(c.lastName)].filter(Boolean).join(' ') || null);
const addr = (l1: string | null, l2: string | null, cp: string | null, city: string | null) => [txt(l1), txt(l2), [txt(cp), txt(city)].filter(Boolean).join(' ')].filter(Boolean).join(', ') || null;

/**
 * Textes à présenter au client pour CE dossier : CGV actives, cases dont la condition est remplie,
 * mention RGPD, formulaire de rétractation si le droit s'applique. Même calcul sur l'appareil,
 * sur le portail et sur le serveur.
 */
export function presentedTexts<T extends TextRef>(
  all: readonly T[],
  ctx: { clientKind: string; dossier: Pick<ContractSource['dossier'], 'reserves' | 'saleChannel' | 'proEmployeeCount' | 'proOutsideMainActivity' | 'earlyStartRequested'>; company: CompanyLegal | null | undefined },
) {
  const active = all.filter((t) => t.isActive !== false).sort((a, b) => a.code.localeCompare(b.code));
  const customerType = customerTypeOf(ctx.clientKind);
  const w = withdrawalRight({ customerType, saleChannel: ctx.dossier.saleChannel, proEmployeeCount: ctx.dossier.proEmployeeCount, proOutsideMainActivity: ctx.dossier.proOutsideMainActivity }, ctx.company);
  const cond = { hasReserves: ctx.dossier.reserves.length > 0, withdrawal: w.applies, earlyStart: ctx.dossier.earlyStartRequested, customerType };
  return {
    customerType,
    withdrawal: w,
    terms: active.filter((t) => t.kind === 'terms'),
    consents: active.filter((t) => t.kind === 'consent' && conditionMet(t.condition, cond)),
    privacy: active.find((t) => t.kind === 'privacy') ?? null,
    withdrawalForm: w.applies ? (active.find((t) => t.kind === 'withdrawal') ?? null) : null,
  };
}

/** Le contenu signé (format 2). Textes RENDUS (variables remplacées) : c'est exactement ce que le client a lu. */
export function contractContent(src: ContractSource) {
  const d = src.dossier;
  const lines = [...src.lines]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id))
    .map((l) => ({
      label: txt(l.label),
      model: txt(l.model),
      unit: l.unit,
      quantity: l.quantity,
      unitPriceHtCents: l.unitPriceHtCents,
      vatRateBp: l.vatRateBp,
      // remise : présente seulement si accordée (le contenu signé sans remise reste identique à l'ancien format)
      ...(l.discountHtCents ? { discountHtCents: l.discountHtCents } : {}),
      // TTC exact : seulement s'il est saisi (contenu signé identique à l'ancien format sinon)
      ...(l.unitPriceTtcCents != null ? { unitPriceTtcCents: l.unitPriceTtcCents } : {}),
      ...(l.discountTtcCents ? { discountTtcCents: l.discountTtcCents } : {}),
      // ancien prix barré : seulement s'il est supérieur au prix (sinon contenu identique à l'ancien format)
      ...(l.compareAtPriceHtCents && l.compareAtPriceHtCents > l.unitPriceHtCents
        ? {
            compareAtPriceHtCents: l.compareAtPriceHtCents,
            ...(l.compareAtPriceTtcCents ? { compareAtPriceTtcCents: l.compareAtPriceTtcCents } : {}),
          }
        : {}),
      totalHtCents: lineTotals({ ...l, discountHtCents: l.discountHtCents ?? 0 }).htCents,
      optionSteps: l.optionSteps.map((s) => txt(s)),
      // mise en avant : seulement si demandée (contenu signé identique à l'ancien format sinon)
      ...(l.quoteHighlight ? { highlight: true as const } : {}),
    }));
  const totals = dossierTotals(src.lines.map((l) => ({ ...l, discountHtCents: l.discountHtCents ?? 0 })));
  const c = src.company ?? {};
  const customerType = customerTypeOf(src.client.kind);
  const w = withdrawalRight({ customerType, saleChannel: d.saleChannel, proEmployeeCount: d.proEmployeeCount, proOutsideMainActivity: d.proOutsideMainActivity }, c);
  const base = {
    'dossier.support': txt(d.installWallTypeLabel),
    'dossier.hauteur': d.installHeightM === null ? null : `${String(d.installHeightM).replace('.', ',')} m`,
    'dossier.acces': d.installAccess ? ACCESS[d.installAccess] ?? d.installAccess : null,
    'client.nom': clientName(src.client),
    'client.adresse': addr(src.client.addressLine1, src.client.addressLine2, src.client.postalCode, src.client.city),
    'commande.date': src.signedAt.slice(0, 10).split('-').reverse().join('/'),
    'commande.objet': lines.map((l) => `${l.quantity} × ${l.label}`).join(' ; ') || null,
  };
  const vars = legalVars(c, base);
  const render = (t: TextRef, extra: Record<string, string | null> = {}) => ({
    id: t.id,
    code: t.code,
    version: t.textVersion,
    title: txt(t.title),
    body: renderLegalText(t.body, { ...vars, ...extra }),
  });
  const reserveTemplates = new Map(src.texts.reserves.map((t) => [t.id, t]));
  return {
    format: 2,
    company: {
      legalName: txt(c.legalName),
      legalForm: txt(c.legalForm),
      siren: txt(c.siren),
      siret: txt(c.siret),
      rcs: txt(c.rcs),
      vatNumber: txt(c.vatNumber),
      address: txt(c.address),
      email: txt(c.contactEmail),
      phone: txt(c.contactPhone),
      // devis émis au nom d'un partenaire : coordonnées du partenaire, pas de logo
      ...(c.issuedFor === 'partner' ? { issuedFor: 'partner' as const } : {}),
    },
    customerType,
    sale: {
      channel: d.saleChannel,
      proEmployeeCount: customerType === 'professional' ? d.proEmployeeCount : null,
      proOutsideMainActivity: customerType === 'professional' ? d.proOutsideMainActivity : null,
      withdrawal: { applies: w.applies, basis: w.basis, days: w.days, rule: w.rule },
      earlyStartRequested: w.applies && d.earlyStartRequested,
    },
    dossier: {
      id: d.id,
      type: txt(d.typeLabel),
      site: { line1: txt(d.siteAddressLine1), line2: txt(d.siteAddressLine2), postalCode: txt(d.sitePostalCode), city: txt(d.siteCity) },
      install: { wallType: txt(d.installWallTypeLabel), atHeight: d.installAtHeight ?? null, access: d.installAccess ?? null, heightM: d.installHeightM ?? null },
    },
    client: {
      kind: src.client.kind,
      civility: src.client.civility ?? null,
      firstName: txt(src.client.firstName),
      lastName: txt(src.client.lastName),
      companyName: txt(src.client.companyName),
      siret: txt(src.client.siret),
      email: src.client.email ? src.client.email.trim().toLowerCase() : null,
      phone: txt(src.client.phone),
      billing: { line1: txt(src.client.addressLine1), line2: txt(src.client.addressLine2), postalCode: txt(src.client.postalCode), city: txt(src.client.city) },
    },
    lines,
    totals: { htCents: totals.htCents, vatCents: totals.vatCents, ttcCents: totals.ttcCents },
    reserves: d.reserves.map((r) => {
      const t = reserveTemplates.get(r.textId);
      return t
        ? { ...render(t, { 'reserve.commentaire': r.comment }), comment: txt(r.comment) }
        : { id: r.textId, code: null, version: null, title: null, body: null, comment: txt(r.comment) };
    }),
    terms: [...src.texts.terms].sort((a, b) => a.code.localeCompare(b.code)).map((t) => render(t)),
    consents: [...src.texts.consents]
      .sort((a, b) => a.code.localeCompare(b.code))
      .map((t) => ({ ...render(t), required: t.required, accepted: src.acceptedConsentIds.includes(t.id) })),
    privacy: src.texts.privacy ? render(src.texts.privacy) : null,
    withdrawalForm: src.texts.withdrawal ? render(src.texts.withdrawal) : null,
    signer: { name: txt(src.signer.name), quality: src.signer.quality },
  };
}
export type ContractContent = ReturnType<typeof contractContent>;

/** JSON canonique : clés triées, pas d'espaces — la base de l'empreinte. */
export function canonicalJson(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v ?? null);
  if (Array.isArray(v)) return `[${v.map(canonicalJson).join(',')}]`;
  const o = v as Record<string, unknown>;
  return `{${Object.keys(o)
    .filter((k) => o[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${canonicalJson(o[k])}`)
    .join(',')}}`;
}

export const utf8 = (s: string) => new TextEncoder().encode(s);

/**
 * Devis non signé envoyé au client pour qu'il voie l'offre (bon de commande sans signature, cases vierges).
 * `email` / `sms` : envoi automatique (service d'envoi configuré) ; `link` : lien à partager depuis le téléphone du
 * commercial (WhatsApp, SMS, e-mail) ou à ouvrir.
 */
export const quoteSendInput = z.object({
  channel: z.enum(['email', 'sms', 'link']),
});
export type QuoteSendInput = z.infer<typeof quoteSendInput>;
/** durée de validité du lien vers le devis (jours) */
export const QUOTE_LINK_DAYS = 30;
